import React, { useMemo, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from "recharts";
import { LuIndianRupee, LuUsers, LuArrowLeftRight } from "react-icons/lu";
import {
  DARK_NAVY,
  DARK_NAVY_LIGHT,
  INPUT_BORDER_FOCUS,
  OFF_WHITE,
  PROFILE_FONT,
} from "../../constants/layout";
import { cardSx } from "../AssetListing/assetListingShared";

const ICON_BLUE = INPUT_BORDER_FOCUS;
const BORDER = "#E8E8E8";

const formatAmount = (value: number) =>
  `₹ ${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const getHoldingTypeLabel = (type: number) => {
  switch (type) {
    case 2:
      return "Mutual Fund";
    case 3:
      return "Fixed Deposit";
    case 1:
      return "Bond";
    case 4:
      return "Equity";
    case 5:
      return "AIF";
    case 6:
      return "PMS";
    case 7:
      return "PPF";
    case 8:
      return "Unlisted Equity";
    default:
      return "NPS";
  }
};

const CHART_COLORS: Record<string, string> = {
  "Mutual Fund": "#1E3A5F",
  Equity: "#08991F",
  AIF: "#E6B800",
  PMS: "#7B5EA7",
  "Fixed Deposit": "#4A90D9",
  "Unlisted Equity": "#E07A2F",
  Bond: "#5A6B7D",
  PPF: "#8B7355",
  NPS: "#D32F2F",
  Others: "#9E9E9E",
};

const PIE_SLICE_COLORS = [
  "#1E3A5F",
  "#08991F",
  "#E6B800",
  "#7B5EA7",
  "#4A90D9",
  "#E07A2F",
  "#5A6B7D",
  "#8B7355",
  "#D32F2F",
];

const getSliceColor = (label: string, index: number) =>
  CHART_COLORS[label] ?? PIE_SLICE_COLORS[index % PIE_SLICE_COLORS.length];

const renderActiveSlice = (props: unknown) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props as {
    cx: number;
    cy: number;
    innerRadius: number;
    outerRadius: number;
    startAngle: number;
    endAngle: number;
    fill: string;
  };
  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius}
      outerRadius={outerRadius + 8}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
      stroke="#ffffff"
      strokeWidth={2}
    />
  );
};

const CHART_SIZE = 260;
const CHART_CENTER = CHART_SIZE / 2;
const PIE_OUTER_RADIUS = 112;
const HOVER_CARD_OFFSET = 36;

const getHoverCardPosition = (
  index: number,
  data: { value: number }[],
) => {
  if (!data.length) {
    return { x: CHART_CENTER, y: CHART_CENTER };
  }

  const sum = data.reduce((acc, item) => acc + item.value, 0);
  if (sum <= 0) {
    return { x: CHART_CENTER, y: CHART_CENTER };
  }

  let cumulative = 0;
  for (let i = 0; i < index; i += 1) {
    cumulative += data[i].value;
  }

  const startAngle = 90 - (cumulative / sum) * 360;
  const endAngle = 90 - ((cumulative + data[index].value) / sum) * 360;
  const midAngle = (startAngle + endAngle) / 2;
  const rad = (Math.PI / 180) * midAngle;
  const distance = PIE_OUTER_RADIUS + HOVER_CARD_OFFSET;

  return {
    x: CHART_CENTER + distance * Math.cos(-rad),
    y: CHART_CENTER + distance * Math.sin(-rad),
  };
};

const HoverSliceCard = ({
  label,
  value,
  total,
}: {
  label: string;
  value: number;
  total: number;
}) => {
  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";

  return (
    <Box
      sx={{
        bgcolor: "#ffffff",
        border: `1px solid ${BORDER}`,
        borderRadius: "10px",
        px: 1.5,
        py: 1,
        boxShadow: "0 6px 20px rgba(0, 0, 0, 0.12)",
        whiteSpace: "nowrap",
      }}
    >
      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, fontWeight: 700, color: DARK_NAVY, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT }}>
        {formatAmount(value)} ({pct}%)
      </Typography>
    </Box>
  );
};

const AVATAR_COLORS = ["#1E3A5F", "#08991F", "#7B5EA7"];
const AVATAR_BGS = ["rgba(30, 58, 95, 0.12)", "rgba(8, 153, 31, 0.12)", "rgba(123, 94, 167, 0.12)"];

type StatCardProps = {
  label: string;
  value: string;
  subtext: string;
  icon: React.ReactNode;
  accent: string;
  accentBg: string;
};

const StatCard = ({ label, value, subtext, icon, accent, accentBg }: StatCardProps) => (
  <Box
    sx={{
      ...cardSx,
      p: 2.5,
      display: "flex",
      alignItems: "flex-start",
      gap: 2,
      flex: 1,
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: "12px",
        bgcolor: accentBg,
        color: accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, color: DARK_NAVY_LIGHT, mb: 0.5 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: PROFILE_FONT,
          fontSize: { xs: 20, md: 24 },
          fontWeight: 700,
          color: DARK_NAVY,
          lineHeight: 1.2,
          wordBreak: "break-word",
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT, mt: 0.5 }}>
        {subtext}
      </Typography>
    </Box>
  </Box>
);

export const PayoutStatsRow = ({
  totalEarnings,
  totalPartners,
  totalTransactions,
  isAdmin = true,
}: {
  totalEarnings: number;
  totalPartners: number;
  totalTransactions: number;
  isAdmin?: boolean;
}) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
      gap: 2,
      mb: 2.5,
    }}
  >
    <StatCard
      label="Total Earnings"
      value={formatAmount(totalEarnings)}
      subtext="All time earnings"
      icon={<LuIndianRupee size={22} />}
      accent="#7B5EA7"
      accentBg="rgba(123, 94, 167, 0.12)"
    />
    <StatCard
      label={isAdmin ? "Total Partners" : "Total Investors"}
      value={String(totalPartners)}
      subtext={isAdmin ? "Active partners" : "Active investors"}
      icon={<LuUsers size={22} />}
      accent="#08991F"
      accentBg="rgba(8, 153, 31, 0.1)"
    />
    <StatCard
      label="Total Transactions"
      value={String(totalTransactions)}
      subtext="Filtered results"
      icon={<LuArrowLeftRight size={22} />}
      accent={ICON_BLUE}
      accentBg="rgba(30, 58, 95, 0.1)"
    />
  </Box>
);

export const TopInvestorsSection = ({ users }: { users: any[] }) => {
  const topInvestors = useMemo(() => {
    const map = new Map<string, { name: string; earnings: number; transactions: number }>();
    const rows = Array.isArray(users) ? users : [];

    rows.forEach((user) => {
      const name = user?.firstName
        ? `${user.firstName} ${user.lastName || ""}`.trim()
        : "Unknown";
      const current = map.get(name) || { name, earnings: 0, transactions: 0 };
      current.earnings += Number(user?.amountToPartner) || 0;
      current.transactions += 1;
      map.set(name, current);
    });

    return Array.from(map.values())
      .sort((a, b) => b.earnings - a.earnings)
      .slice(0, 3);
  }, [users]);

  const totalEarnings = topInvestors.reduce((sum, item) => sum + item.earnings, 0);

  return (
    <Box sx={{ ...cardSx, p: 2.5, height: "100%" }}>
      <Typography
        sx={{ fontFamily: PROFILE_FONT, fontSize: 16, fontWeight: 700, color: DARK_NAVY, mb: 2 }}
      >
        Top 3 Investors
      </Typography>

      {topInvestors.length === 0 ? (
        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
          No investor data available
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: `repeat(${Math.min(topInvestors.length, 3)}, 1fr)`,
            },
            gap: 1.5,
          }}
        >
          {topInvestors.map((investor, index) => {
            const initial = (investor.name?.trim()?.[0] || "?").toUpperCase();
            const sharePct =
              totalEarnings > 0 ? ((investor.earnings / totalEarnings) * 100).toFixed(1) : "0.0";

            return (
              <Box
                key={investor.name}
                sx={{
                  borderRadius: "12px",
                  border: `1px solid ${BORDER}`,
                  bgcolor: "#FAFAFA",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  minHeight: 168,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    bgcolor: AVATAR_BGS[index] || OFF_WHITE,
                    color: AVATAR_COLORS[index] || ICON_BLUE,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: PROFILE_FONT,
                    fontSize: 18,
                    fontWeight: 700,
                    mb: 1.25,
                  }}
                >
                  {initial}
                </Box>

                <Typography
                  sx={{
                    fontFamily: PROFILE_FONT,
                    fontSize: 14,
                    fontWeight: 600,
                    color: DARK_NAVY,
                    mb: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                  }}
                >
                  {investor.name}
                </Typography>

                <Typography
                  sx={{ fontFamily: PROFILE_FONT, fontSize: 11, color: DARK_NAVY_LIGHT, mb: 0.25 }}
                >
                  Total Earnings
                </Typography>
                <Typography
                  sx={{
                    fontFamily: PROFILE_FONT,
                    fontSize: 15,
                    fontWeight: 700,
                    color: DARK_NAVY,
                    mb: 1,
                  }}
                >
                  {formatAmount(investor.earnings)}
                </Typography>

                <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT }}>
                  {investor.transactions} transaction{investor.transactions !== 1 ? "s" : ""} · {sharePct}%
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export const EarningsByProductChart = ({ users }: { users: any[] }) => {
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);

  const { pieData, total } = useMemo(() => {
    const totals = new Map<string, number>();
    const rows = Array.isArray(users) ? users : [];

    rows.forEach((user) => {
      const label = getHoldingTypeLabel(Number(user?.holdingType));
      const amount =
        Number(user?.amountToPartner) ||
        Number(user?.brockerage) ||
        0;
      if (amount <= 0) return;
      totals.set(label, (totals.get(label) || 0) + amount);
    });

    const data = Array.from(totals.entries())
      .map(([label, value], index) => ({
        label,
        value,
        fill: getSliceColor(label, index),
      }))
      .sort((a, b) => b.value - a.value)
      .map((item, index) => ({
        ...item,
        fill: getSliceColor(item.label, index),
      }));

    const sum = data.reduce((acc, item) => acc + item.value, 0);
    return { pieData: data, total: sum };
  }, [users]);

  const hasMultipleSlices = pieData.length > 1;
  const activeItem = activeIndex !== undefined ? pieData[activeIndex] : undefined;
  const hoverCardPosition =
    activeIndex !== undefined ? getHoverCardPosition(activeIndex, pieData) : null;

  return (
    <Box sx={{ ...cardSx, p: 2.5, height: "100%", overflow: "visible" }}>
      <Typography
        sx={{ fontFamily: PROFILE_FONT, fontSize: 16, fontWeight: 700, color: DARK_NAVY, mb: 2 }}
      >
        Earnings by Product Type
      </Typography>

      {pieData.length === 0 ? (
        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, color: DARK_NAVY_LIGHT }}>
          No earnings data available
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "center", md: "flex-start" },
            gap: 3,
            overflow: "visible",
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: CHART_SIZE,
              height: CHART_SIZE,
              flexShrink: 0,
              overflow: "visible",
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={68}
                  outerRadius={PIE_OUTER_RADIUS}
                  paddingAngle={hasMultipleSlices ? 2 : 0}
                  minAngle={hasMultipleSlices ? 4 : 0}
                  stroke={hasMultipleSlices ? "#ffffff" : "none"}
                  strokeWidth={hasMultipleSlices ? 1 : 0}
                  isAnimationActive={false}
                  startAngle={90}
                  endAngle={-270}
                  activeIndex={activeIndex}
                  activeShape={renderActiveSlice}
                  onMouseEnter={(_, index) => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(undefined)}
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`${entry.label}-${index}`}
                      fill={entry.fill}
                      stroke={entry.fill}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {activeItem && hoverCardPosition ? (
              <Box
                sx={{
                  position: "absolute",
                  left: hoverCardPosition.x,
                  top: hoverCardPosition.y,
                  transform: "translate(-50%, -50%)",
                  zIndex: 20,
                  pointerEvents: "none",
                }}
              >
                <HoverSliceCard
                  label={activeItem.label}
                  value={activeItem.value}
                  total={total}
                />
              </Box>
            ) : null}

            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                width: 110,
                pointerEvents: "none",
              }}
            >
              <Typography
                sx={{ fontFamily: PROFILE_FONT, fontSize: 11, color: DARK_NAVY_LIGHT, mb: 0.25 }}
              >
                Total
              </Typography>
              <Typography
                sx={{
                  fontFamily: PROFILE_FONT,
                  fontSize: 13,
                  fontWeight: 700,
                  color: DARK_NAVY,
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                }}
              >
                {formatAmount(total)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ flex: 1, width: "100%", minWidth: 0 }}>
            <Grid container spacing={1.5}>
              {pieData.map((item) => {
                const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0.0";
                return (
                  <Grid item xs={12} sm={6} key={item.label}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: item.fill,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{ fontFamily: PROFILE_FONT, fontSize: 13, fontWeight: 600, color: DARK_NAVY }}
                        >
                          {item.label}
                        </Typography>
                        <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT }}>
                          {formatAmount(item.value)} ({pct}%)
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
      )}
    </Box>
  );
};
